import React, { useState, useRef, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Slider } from './components/ui/slider';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Progress } from './components/ui/progress';
import { Alert, AlertDescription } from './components/ui/alert';
import { Upload, Package, Palette, Sliders, Calculator, Download, AlertTriangle, CheckCircle, Info, Eye, Layers } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { axiosWithRetry, fetchWithRetry } from './utils/withRetry';
import { useElapsedTimer } from './hooks/useElapsedTimer';
import { API, ODOO_BASE_URL, ODOO_ADD_TO_CART_URL, ODOO_CART_RETURN_URL } from './constants';
import SlicerPanel from './components/SlicerPanel';
import STLFileList from './components/STLFileList';
import { AutoCameraPosition, SimpleBuildPlate, SimpleSTLMesh } from './components/Viewer3D';
import STLValidationCard from './components/STLValidationCard';
import FileUpload from './components/FileUpload';
import MaterialSelector from './components/MaterialSelector';
import ScaleControl from './components/ScaleControl';
import InfillControl from './components/InfillControl';
import PriceDisplay from './components/PriceDisplay';
import ErrorBoundary from './components/ErrorBoundary';

function isWebglAvailable() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (error) {
    return false;
  }
}

function App() {
  // Multi-STL Support
  const [stlFiles, setStlFiles] = useState([]); 
  const [currentFileIndex, setCurrentFileIndex] = useState(null);
  
  // Current file data (derived from stlFiles array)
  const currentFile = currentFileIndex !== null ? stlFiles[currentFileIndex] : null;
  const stlAnalysis = currentFile?.analysis || null;
  const stlUrl = stlAnalysis ? `${API}/stl-download/${stlAnalysis.id}` : null;
  const quote = currentFile?.quote || null;
  const slicerResults = currentFile?.slicerResults || null;
  
  // Global states
  const [materials, setMaterials] = useState(null);
  const [printerLimits, setPrinterLimits] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [infill, setInfill] = useState(20.0);
  const [quantity, setQuantity] = useState(1);
  const [show3DPreview, setShow3DPreview] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [slicerProgress, setSlicerProgress] = useState(0);
  const [slicerElapsed, setSlicerElapsed] = useState(0);
  const [slicerEstimated, setSlicerEstimated] = useState(30);
  const [isSlicing, setIsSlicing] = useState(false);
  const [isQuoteStale, setIsQuoteStale] = useState(false);
  const [uploadElapsed, setUploadElapsed] = useState(0);
  const [uploadFileSize, setUploadFileSize] = useState(0);
  const uploadStartRef = React.useRef(null);
  const processingStartElapsedRef = React.useRef(null);
  const uploadCancelRef = React.useRef(null); // set by <FileUpload cancelRef=…>
  const inlineUploadAbortRef = React.useRef(null); // for inline 'add more' upload
  const [uploadComplete, setUploadComplete] = useState(false);
  const [lostFilesCount, setLostFilesCount] = useState(0);

  // Check if current file is complete (material, color, slicer done)
  const isCurrentFileComplete = () => {
    if (currentFileIndex === null || stlFiles.length === 0) {
      return false;
    }
    const currentFile = stlFiles[currentFileIndex];
    return !!(currentFile?.material && currentFile?.color && currentFile?.slicerResults);
  };

  // Check if we can upload a new file
  const canUploadNewFile = () => {
    // If no files yet, upload is always allowed
    if (stlFiles.length === 0) {
      return true;
    }
    // If files exist, current file must be complete
    return isCurrentFileComplete();
  };

  // Get missing steps for current file
  const getMissingSteps = () => {
    if (currentFileIndex === null || stlFiles.length === 0) {
      return [];
    }
    const currentFile = stlFiles[currentFileIndex];
    const missing = [];
    if (!currentFile?.material) missing.push('Material auswählen');
    if (!currentFile?.color) missing.push('Farbe auswählen');
    if (!currentFile?.slicerResults) missing.push('Slicer-Berechnung abwarten');
    return missing;
  };

  const webglAvailable = typeof window !== "undefined" ? isWebglAvailable() : true;

  // ODOO_ADD_TO_CART_URL / ODOO_CART_RETURN_URL / ODOO_BASE_URL imported from ./constants

  React.useEffect(() => {
    // Load materials and printer limits on app start
    const loadInitialData = async () => {
      try {
        const [materialsRes, limitsRes] = await Promise.all([
          axiosWithRetry({ method: 'get', url: `${API}/materials` }),
          axiosWithRetry({ method: 'get', url: `${API}/printer-limits` })
        ]);
        setMaterials(materialsRes.data);
        setPrinterLimits(limitsRes.data);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Error loading initial data:', error);
        toast.error('Fehler beim Laden der Materialdaten');
      }
    };
    
    loadInitialData();
  }, []);

  // Ref: true once the restore-on-mount attempt has finished.
  // The save-effect must NOT fire before that, otherwise it would overwrite
  // the stored data with the initial empty state (race condition on mount).
  const restoreDoneRef = React.useRef(false);

  // Upload elapsed timer – syncs setUploadElapsed every 500ms while uploading
  useElapsedTimer(isUploading, (s) => setUploadElapsed(s));

  // Restore state from localStorage on mount (back-button / page refresh)
  React.useEffect(() => {
    const restore = async () => {
      try {
        const savedRaw = localStorage.getItem('3dConfiguratorState');
        if (!savedRaw) { restoreDoneRef.current = true; return; }
        const state = JSON.parse(savedRaw);
        if (state.schemaVersion !== 1) {
          // Incompatible schema – clear stale state
          localStorage.removeItem('3dConfiguratorState');
          restoreDoneRef.current = true;
          return;
        }
        if (!state.stlFiles || state.stlFiles.length === 0) { restoreDoneRef.current = true; return; }

        if (process.env.NODE_ENV !== "production") console.log('Restoring state from localStorage…', state);

        // Restore files – URL is derived at render time from analysis.id,
        // no need to fetch vertices from the backend anymore.
        const restored = state.stlFiles.map((f) => {
          if (!f.analysis) return null;
          return {
            analysis: f.analysis,
            vertices: null,
            quote: f.quote || null,
            slicerResults: f.slicerResults || null,
            material: f.material || null,
            color: f.color || null,
            scale: f.scale || 1.0,
            infill: f.infill || 20.0,
            quantity: f.quantity || 1,
            fileObject: null
          };
        });

        const restoredFiltered = restored.filter(Boolean);

        // ── UX1: probe each file's availability on the server ──────────────
        const availabilityResults = await Promise.all(
          restoredFiltered.map(async (f) => {
            if (!f.analysis?.id) return false;
            try {
              const r = await fetch(`${API}/stl-download/${f.analysis.id}`, { method: 'HEAD' });
              return r.ok;
            } catch {
              return false;
            }
          })
        );
        const valid = restoredFiltered.filter((_, i) => availabilityResults[i]);
        const lostCount = restoredFiltered.length - valid.length;
        if (lostCount > 0) setLostFilesCount(lostCount);

        if (valid.length > 0) {
          setStlFiles(valid);
          const idx = (state.currentFileIndex != null && state.currentFileIndex < valid.length)
                ? state.currentFileIndex : 0;
          setCurrentFileIndex(idx);
          const cur = valid[idx];
          setSelectedMaterial(cur.material || state.selectedMaterial || null);
          setSelectedColor(cur.color || state.selectedColor || null);
          setScale(cur.scale || state.scale || 1.0);
          setInfill(cur.infill || state.infill || 20.0);
          setQuantity(cur.quantity || state.quantity || 1);
          setUploadComplete(true);
          // Fetch binary STL blob so SlicerPanel can recalculate after restore
          if (cur.analysis?.id) {
            try {
              const resp = await axiosWithRetry({ method: 'get', url: `${API}/stl-download/${cur.analysis.id}`, responseType: 'blob' });
              const reconstructed = new File([resp.data], cur.analysis.filename || 'model.stl', { type: 'application/octet-stream' });
              setSelectedFile(reconstructed);
            } catch(e) { console.warn('Could not fetch STL for slicer after restore:', e); }
          }
          if (lostCount > 0) {
            toast.warning(`${valid.length} Datei(en) wiederhergestellt, ${lostCount} nicht mehr verfügbar.`);
          } else {
            toast.success(`${valid.length} Datei(en) wiederhergestellt`);
          }
        } else if (lostCount > 0) {
          // All files lost – clear stale localStorage
          try { localStorage.removeItem('3dConfiguratorState'); } catch(e) {}
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Error restoring from localStorage:', error);
        try { localStorage.removeItem('3dConfiguratorState'); } catch(e) {}
      } finally {
        restoreDoneRef.current = true;
      }
    };
    restore();
  }, []); // run once on mount

  // Save full state to localStorage – but only AFTER restore is done,
  // so the initial mount with empty state never overwrites persisted data.
  React.useEffect(() => {
    if (!restoreDoneRef.current) return;
    try {
      const stateToSave = {
        stlFiles: stlFiles.map(f => ({
          analysis: f.analysis,
          material: f.material,
          color: f.color,
          scale: f.scale,
          infill: f.infill,
          quantity: f.quantity || 1,
          quote: f.quote,
          slicerResults: f.slicerResults
        })),
        currentFileIndex,
        selectedMaterial,
        selectedColor,
        scale,
        infill,
        quantity
      };
      stateToSave.schemaVersion = 1;
      localStorage.setItem('3dConfiguratorState', JSON.stringify(stateToSave));
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error saving to localStorage:', error);
    }
  }, [stlFiles, currentFileIndex, selectedMaterial, selectedColor, scale, infill, quantity]);

  const handleFileUploaded = async (analysis, fileObj = null) => {
    if (process.env.NODE_ENV !== "production") console.log("STL file uploaded successfully:", analysis);
    if (process.env.NODE_ENV !== "production") console.log("File object received:", fileObj ? fileObj.name : 'null');
    setIsUploading(false);
    setUploadComplete(true);
    setUploadProgress(100);
    
    // Reset material and color when adding 2nd+ file (force user to reselect)
    if (stlFiles.length > 0) {
      if (process.env.NODE_ENV !== "production") console.log("Resetting material and color for new file upload");
      setSelectedMaterial(null);
      setSelectedColor(null);
      toast.info('Bitte wählen Sie Material und Farbe für die neue Datei aus');
    }
    
    // Use provided fileObj or fall back to selectedFile
    const fileToStore = fileObj || selectedFile;
    if (process.env.NODE_ENV !== "production") console.log("File to store:", fileToStore ? fileToStore.name : 'null');
    
    // Create new file object – the 3D viewer loads the binary STL
    // directly via STLLoader, no vertices needed here.
    try {
      const newFile = {
        analysis: analysis,
        vertices: null,
        quote: null,
        slicerResults: null,
        material: selectedMaterial,
        color: selectedColor,
        scale: scale,
        infill: infill,
        quantity: quantity,
        fileObject: fileToStore  //Store the original File object
      };
      
      if (process.env.NODE_ENV !== "production") console.log("Created new file entry with fileObject:", !!newFile.fileObject);
      
      // Add to files array and select it
      setStlFiles(prev => [...prev, newFile]);
      setCurrentFileIndex(stlFiles.length);
      
      toast.success(`${analysis.filename} hinzugefügt`);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error loading STL data:', error);
      toast.error('Fehler beim Laden der 3D-Daten');
    }
  };
  
  // Select a different file from the list
  const handleSelectFile = async (index) => {
    setCurrentFileIndex(index);
    const file = stlFiles[index];
    if (file) {
      setSelectedMaterial(file.material);
      setSelectedColor(file.color);
      setScale(file.scale);
      setInfill(file.infill);
      setQuantity(file.quantity || 1);
      // Update selectedFile so SlicerPanel gets the correct file object
      if (file.fileObject) {
        setSelectedFile(file.fileObject);
      } else if (file.analysis?.id) {
        try {
          const resp = await axiosWithRetry({ method: 'get', url: `${API}/stl-download/${file.analysis.id}`, responseType: 'blob' });
          const reconstructed = new File([resp.data], file.analysis.filename || 'model.stl', { type: 'application/octet-stream' });
          setSelectedFile(reconstructed);
        } catch(e) {
          const status = e?.response?.status;
          if (status === 404) {
            toast.warning(`STL-Datei "${file.analysis?.filename || ''}" ist nicht mehr verfügbar. Bitte erneut hochladen.`);
          } else {
            console.warn('Could not fetch STL for slicer:', e);
          }
        }
      }
      // Mark upload as complete so SlicerPanel triggers immediately
      setUploadComplete(true);
    }
  };
  
  // Remove a file from the list
  const handleRemoveFile = (index) => {
    setStlFiles(prev => prev.filter((_, i) => i !== index));
    if (currentFileIndex === index) {
      setCurrentFileIndex(stlFiles.length > 1 ? 0 : null);
    } else if (currentFileIndex > index) {
      setCurrentFileIndex(currentFileIndex - 1);
    }
    toast.success('Datei entfernt');
  };
  
  // Update material and sync to current file
  const handleMaterialChange = (material) => {
    setSelectedMaterial(material);
    if (currentFileIndex !== null) {
      setStlFiles(prev => {
        const updated = [...prev];
        updated[currentFileIndex] = {
          ...updated[currentFileIndex],
          material: material
        };
        return updated;
      });
    }
  };
  
  // Update color and sync to current file
  const handleColorChange = (color) => {
    setSelectedColor(color);
    if (currentFileIndex !== null) {
      setStlFiles(prev => {
        const updated = [...prev];
        updated[currentFileIndex] = {
          ...updated[currentFileIndex],
          color: color
        };
        return updated;
      });
    }
  };
  
  // Update scale and sync to current file
  const handleScaleChange = (newScale) => {
    setScale(newScale);
    if (currentFileIndex !== null) {
      setStlFiles(prev => {
        const updated = [...prev];
        updated[currentFileIndex] = {
          ...updated[currentFileIndex],
          scale: newScale
        };
        return updated;
      });
    }
  };
  
  // Update infill and sync to current file
  const handleInfillChange = (newInfill) => {
    setInfill(newInfill);
    if (currentFileIndex !== null) {
      setStlFiles(prev => {
        const updated = [...prev];
        updated[currentFileIndex] = {
          ...updated[currentFileIndex],
          infill: newInfill
        };
        return updated;
      });
    }
  };

  // Update quantity for a specific file by index
  const handleFileQuantityChange = (fileIndex, newQty) => {
    const q = Math.max(1, Math.min(999, parseInt(newQty, 10) || 1));
    setStlFiles(prev => {
      const updated = [...prev];
      updated[fileIndex] = {
        ...updated[fileIndex],
        quantity: q
      };
      return updated;
    });
    // Keep quantity state in sync if this is the currently viewed file
    if (fileIndex === currentFileIndex) {
      setQuantity(q);
    }
  };


  const calculateQuote = async () => {
    if (!stlAnalysis || !selectedMaterial || !selectedColor) {
      if (process.env.NODE_ENV !== "production") console.log("Missing data for quote calculation:", { stlAnalysis: !!stlAnalysis, selectedMaterial, selectedColor });
      return;
    }

    if (process.env.NODE_ENV !== "production") console.log("Calculating quote with:", { stlAnalysis, selectedMaterial, selectedColor, scale });
    setIsCalculating(true);
    
    try {
      const materialType = materials.resin[selectedMaterial] ? 'resin' : 'filament';
      if (process.env.NODE_ENV !== "production") console.log("Material type determined:", materialType);
        const quoteRequest = {
          stl_id: stlAnalysis.id,
          material_type: materialType,
          material: selectedMaterial,
          color: selectedColor,
          scale_factor: slicerResults?.input_params?.scale_factor || scale,
          infill_percent: infill,
          slicer_weight_g: slicerResults?.estimates?.filament_weight_g || null,
          slicer_print_time_hours: slicerResults?.estimates?.print_time_hours || null
        };
      
      if (process.env.NODE_ENV !== "production") console.log("Sending quote request:", quoteRequest);
      
      const response = await axiosWithRetry({ method: 'post', url: `${API}/quote`, data: quoteRequest });
      
      if (process.env.NODE_ENV !== "production") console.log("Quote response received:", response.data);
      
      // Update quote in current file
      if (currentFileIndex !== null) {
        setStlFiles(prev => {
          const updated = [...prev];
          updated[currentFileIndex] = {
            ...updated[currentFileIndex],
            quote: response.data
          };
          return updated;
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error calculating quote:', error);
      if (error.response?.data?.detail) {
        toast.error(`Fehler: ${error.response.data.detail}`);
      } else {
        toast.error('Fehler bei der Preisberechnung');
      }
    } finally {
      setIsCalculating(false);
      setIsQuoteStale(false);
    }
  };

  // Set infill to 100% for resin materials
  React.useEffect(() => {
    if (materials && selectedMaterial && materials.resin && materials.resin[selectedMaterial]) {
      if (infill !== 100) {
        setInfill(100);
      }
    }
  }, [selectedMaterial, materials, infill]);

  // Keep a ref to calculateQuote so the debounce timer always calls the
  // latest version (avoids stale-closure issues with setTimeout).
  const calculateQuoteRef = React.useRef(calculateQuote);
  calculateQuoteRef.current = calculateQuote;

  // Recalculate quote when parameters change – debounced 300ms to avoid
  // hammering the backend on every slider tick.
  React.useEffect(() => {
    if (!(stlAnalysis && selectedMaterial && selectedColor && slicerResults)) return;
    setIsQuoteStale(true);
    const timer = setTimeout(() => {
      calculateQuoteRef.current();
    }, 300);
    return () => clearTimeout(timer);
  }, [currentFileIndex, stlAnalysis, selectedMaterial, selectedColor, infill, slicerResults]);
  
  // Add all files to cart
  const handleAddAllToCart = async () => {
    if (isSlicing || isCalculating || isQuoteStale) {
      toast.error('Bitte warten Sie, bis die Slicer-Berechnung und Preisberechnung abgeschlossen sind.');
      return;
    }
    // Filter files that have quotes (fileObject can be null for restored files)
    const filesWithQuotes = stlFiles.filter(f => f.quote && f.material && f.color);
    
    if (process.env.NODE_ENV !== "production") console.log('Files to add to cart:', filesWithQuotes.length);
    filesWithQuotes.forEach((f, idx) => {
      if (process.env.NODE_ENV !== "production") console.log(`File ${idx + 1}:`, {
        filename: f.analysis.filename,
        material: f.material,
        color: f.color,
        price: f.quote?.price,
        hasFileObject: !!f.fileObject
      });
    });
    
    if (filesWithQuotes.length === 0) {
      toast.error('Keine Dateien mit Preisberechnung vorhanden');
      return;
    }

    try {
      const total = filesWithQuotes.length;
      const progressToastId = toast.loading(`Füge Datei 1 von ${total} hinzu…`);
      
      let successCount = 0;
      
      // Add each file to cart
      for (const file of filesWithQuotes) {
        const fileIdx = filesWithQuotes.indexOf(file) + 1;
        toast.loading(`Füge Datei ${fileIdx} von ${total} hinzu: ${file.analysis.filename}`, { id: progressToastId });

        const materialType = materials.resin[file.material] ? 'resin' : 'filament';

        // If fileObject is null (restored from localStorage), re-fetch the STL from the server
        let stlFileObj = file.fileObject;
        if (!stlFileObj) {
          try {
            const dlRes = await fetchWithRetry(`${API}/stl-download/${file.analysis.id}`);
            if (!dlRes.ok) throw new Error(`Download failed: ${dlRes.status}`);
            const blob = await dlRes.blob();
            stlFileObj = new File([blob], file.analysis.filename, { type: 'application/octet-stream' });
          } catch (dlErr) {
            if (process.env.NODE_ENV !== 'production') console.error('Could not re-fetch STL:', dlErr);
            toast.error(`Datei ${file.analysis.filename} konnte nicht geladen werden`);
            continue;
          }
        }
        
        const formData = new FormData();
        formData.append('stl_file', stlFileObj);
        formData.append('material', file.material);
        formData.append('material_type', materialType);
        formData.append('color', file.color);
        const fileQty = file.quantity || 1;
        formData.append('scale_factor', file.scale.toString());
        formData.append('infill_percent', file.infill.toString());
        formData.append('quantity', fileQty.toString());
        formData.append('price', (Number(file.quote.price) * fileQty).toFixed(2));
        formData.append('return_url', ODOO_CART_RETURN_URL);

        // Submit to Odoo
        const response = await fetch(ODOO_ADD_TO_CART_URL, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });

        if (!response.ok) {
          if (process.env.NODE_ENV !== 'production') console.error(`Failed to add ${file.analysis.filename}:`, response.status);
          throw new Error(`Failed to add ${file.analysis.filename}`);
        }
        
        successCount++;


        // Small delay between requests to avoid race conditions
        if (successCount < total) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      toast.success(`${successCount} von ${total} Datei(en) zum Warenkorb hinzugefügt`, { id: progressToastId });
      
      setTimeout(() => {
        window.location.href = ODOO_CART_RETURN_URL;
      }, 1500);
      
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Cart error:', error);
      toast.error('Fehler beim Hinzufügen zum Warenkorb: ' + error.message);
    }
  };

  const materialType = materials && selectedMaterial ? 
    (materials.resin[selectedMaterial] ? 'resin' : 'filament') : 'resin';

  const handleFileSelected = (file) => {
    setSelectedFile(file);
    setUploadFileSize(file?.size || 0);
  };

  const handleUploadStart = () => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadComplete(false);
    setSlicerProgress(0);
    setIsSlicing(false);
    setUploadElapsed(0);
    processingStartElapsedRef.current = null;
    uploadStartRef.current = Date.now();
  };

  const handleUploadProgress = (percent) => {
    if (typeof percent === 'number') {
      if (percent >= 99 && processingStartElapsedRef.current === null) {
        processingStartElapsedRef.current = Math.round((Date.now() - uploadStartRef.current) / 1000);
      }
      setUploadProgress(percent);
    }
  };

  const handleUploadComplete = () => {
    setUploadComplete(true);
    setUploadProgress(100);
    setIsUploading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-stone-100">
      <Toaster position="top-right" richColors />
      <div className="flex-1 max-w-[1920px] w-full mx-auto px-3 sm:px-6 py-4 sm:py-8 pb-24">
        {/* ── Branded Header ── */}
        <div className="flex items-center justify-between mb-4 sm:mb-8 bg-sky-100 rounded-xl px-4 sm:px-6 py-3 sm:py-4 shadow-sm border border-sky-200">
          <div className="flex items-center gap-3 sm:gap-4">
            <img src="/logo.webp" alt="3D Fabrik Logo" className="h-10 sm:h-14 w-auto" />
            <div className="border-l border-gray-400 pl-3 sm:pl-4">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
                3D-Druck Konfigurator
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                STL-Datei hochladen, prüfen und direkt ein Angebot erhalten
              </p>
            </div>
          </div>
          <a
            href={ODOO_BASE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-2 text-xs text-gray-600 hover:text-red-700 transition-colors"
          >
            <span>Zum Shop</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
          </a>
        </div>

        {/* ── UX1: Lost-files banner ── */}
        {lostFilesCount > 0 && (
          <Alert className="mb-4 border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="flex items-center justify-between gap-4">
              <span>
                <strong>{lostFilesCount} Datei{lostFilesCount === 1 ? '' : 'en'} nicht mehr verfügbar.</strong>{' '}
                Die gespeicherten STL-{lostFilesCount === 1 ? 'Datei ist' : 'Dateien sind'} auf dem Server abgelaufen.
                Bitte {lostFilesCount === 1 ? 'sie' : 'diese'} erneut hochladen.
              </span>
              <button
                onClick={() => setLostFilesCount(0)}
                className="shrink-0 text-amber-700 hover:text-amber-900 underline text-sm whitespace-nowrap"
              >
                Schließen
              </button>
            </AlertDescription>
          </Alert>
        )}

        <div className={`grid gap-3 sm:gap-6 ${stlFiles.length > 0 ? 'grid-cols-1 lg:grid-cols-5' : 'grid-cols-1 lg:grid-cols-3'}`}>
          {/* Left Sidebar - STL File List */}
          {stlFiles.length > 0 && (
            <div className="order-3 lg:order-1 lg:col-span-1 self-start">
              <STLFileList 
                files={stlFiles}
                currentIndex={currentFileIndex}
                onSelectFile={handleSelectFile}
                onRemoveFile={handleRemoveFile}
                onQuantityChange={handleFileQuantityChange}
                onAddAllToCart={handleAddAllToCart}
                isSlicing={isSlicing}
                isQuoteStale={isQuoteStale}
                isCalculating={isCalculating}
              />
            </div>
          )}

          <div className="order-2 lg:order-2 lg:col-span-3 space-y-6">
            {stlFiles.length === 0 ? (
              <>
              <FileUpload
                onFileUploaded={handleFileUploaded}
                onFileSelected={handleFileSelected}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                uploadElapsed={uploadElapsed}
                uploadFileSize={uploadFileSize}
                onUploadStart={handleUploadStart}
                onUploadProgress={handleUploadProgress}
                onUploadComplete={handleUploadComplete}
                cancelRef={uploadCancelRef}
                />

                {/* Upload Instructions Card - Always visible */}
                <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-gray-900">
                      <Info className="w-5 h-5" />
                      So funktioniert der Upload-Prozess
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-gray-700 space-y-4">
                    <p className="text-sm font-medium text-gray-800">
                      Für beste Ergebnisse folgen Sie bitte diesen Schritten für jede hochgeladene Datei:
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          1
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">STL-Datei hochladen</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Ziehen Sie Ihre STL-Datei per Drag & Drop in das Upload-Feld oder klicken Sie, um eine Datei auszuwählen. 
                            Die Datei wird automatisch auf Druckbarkeit geprüft.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          2
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Material und Farbe wählen</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Wählen Sie im rechten Panel das gewünschte Druckverfahren, Material und die Farbe aus. 
                            Diese Auswahl beeinflusst die Druckqualität und den Preis.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          3
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Slicer-Berechnung abwarten</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Nach der Material-Auswahl startet automatisch die Slicer-Berechnung. 
                            Sie sehen die voraussichtliche Druckzeit, benötigtes Material und den finalen Preis.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          ✓
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Weitere Dateien hinzufügen (optional)</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Erst nachdem alle 3 Schritte abgeschlossen sind, können Sie weitere STL-Dateien hochladen. 
                            Dies verhindert Preisfehler und stellt sicher, dass jedes Teil korrekt konfiguriert ist.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex gap-2 items-start">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-yellow-800">
                          <strong>Wichtig:</strong> Sie müssen jeden Upload vollständig abschließen 
                          (Material, Farbe, Slicer-Berechnung), bevor Sie die nächste Datei hochladen können. 
                          Dies garantiert korrekte Preisberechnungen für jedes Teil.
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-orange-50 border border-orange-300 rounded-lg">
                      <div className="flex gap-2 items-start">
                        <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-orange-900">
                          <strong>Hinweis bei großen Dateien (ab 50 MB):</strong> Das Hochladen und Verarbeiten von STL-Dateien ab 50 MB kann 
                          deutlich länger dauern – in manchen Fällen <strong>bis zu 5 Minuten</strong>. 
                          Bitte haben Sie Geduld und schließen Sie das Browserfenster während des Uploads nicht.
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* Upload Progress Guard - Show missing steps */}
                {stlFiles.length > 0 && !canUploadNewFile() && (
                  <Card className="bg-yellow-50 border-yellow-300">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-yellow-900">Aktuelle Datei noch nicht vollständig</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            Bitte schließen Sie diese Schritte ab, bevor Sie die nächste Datei hochladen:
                          </p>
                          <ul className="text-sm text-yellow-700 mt-2 list-disc list-inside space-y-1">
                            {getMissingSteps().map((step, idx) => (
                              <li key={idx}>{step}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Compact Add Another File Button */}
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    {isUploading && (
                      <div className="mb-3 space-y-1">
                        <Progress value={uploadProgress || 0} className="w-full" />
                        <div className="flex justify-between items-center text-xs text-red-700">
                          <span>{uploadProgress || 0}%</span>
                          <span>
                            {uploadProgress >= 99
                              ? 'Verarbeite Datei…'
                              : 'Datei wird hochgeladen…'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-right">
                              {(() => {
                                if (uploadProgress >= 99) {
                                  const procEst = Math.max(3, Math.round((uploadFileSize / 1024 / 1024) * 3));
                                  return `Noch ~${procEst}s`;
                                }
                                if (uploadProgress > 2) {
                                  const rem = Math.round(uploadElapsed * (100 - uploadProgress) / uploadProgress);
                                  if (rem > 60) return `Noch ~${Math.round(rem / 60)}min`;
                                  if (rem > 0) return `Noch ~${rem}s`;
                                }
                                return `${uploadElapsed}s`;
                              })()}
                            </span>
                            <button
                              onClick={() => {
                                if (inlineUploadAbortRef.current) {
                                  inlineUploadAbortRef.current.abort();
                                  inlineUploadAbortRef.current = null;
                                }
                                handleUploadProgress(0);
                                handleUploadComplete();
                              }}
                              className="flex items-center gap-1 px-2 py-0.5 bg-white hover:bg-red-100 hover:text-red-800 border border-red-300 rounded transition-colors whitespace-nowrap"
                            >
                              ✕ Abbrechen
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Weitere STL-Datei hinzufügen</h4>
                        <p className="text-sm text-gray-600">
                          {canUploadNewFile() 
                            ? 'Laden Sie mehrere Dateien hoch für eine gemeinsame Bestellung' 
                            : 'Schließen Sie zuerst die aktuelle Datei ab (Material, Farbe, Slicer-Berechnung)'}
                        </p>
                      </div>
                      <div className="relative">
                        <input
                          type="file"
                          accept=".stl"
                          className="hidden"
                          id="add-more-file"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              // Check if upload is allowed
                              if (!canUploadNewFile()) {
                                toast.error('Bitte schließen Sie die aktuelle Datei ab: ' + getMissingSteps().join(', '));
                                e.target.value = '';
                                return;
                              }
                              const fileUploadHandler = async (f) => {
                                if (!f.name.endsWith('.stl')) {
                                  toast.error('Bitte wählen Sie eine STL-Datei aus');
                                  return;
                                }
                                const formData = new FormData();
                                formData.append('file', f);
                                const controller = new AbortController();
                                inlineUploadAbortRef.current = controller;
                                try {
                                  handleFileSelected(f);
                                  handleUploadStart();
                                  const response = await axiosWithRetry({
                                    method: 'post',
                                    url: `${API}/upload-stl`,
                                    data: formData,
                                    signal: controller.signal,
                                    onUploadProgress: (event) => {
                                      if (event.total) {
                                        const percent = Math.round((event.loaded / event.total) * 100);
                                        handleUploadProgress(percent);
                                      }
                                    },
                                  });
                                  inlineUploadAbortRef.current = null;
                                  handleUploadComplete();
                                  await handleFileUploaded(response.data, f);
                                } catch (error) {
                                  inlineUploadAbortRef.current = null;
                                  if (error.name === 'CanceledError' || error.name === 'AbortError') {
                                    handleUploadProgress(0);
                                    handleUploadComplete();
                                    return;
                                  }
                                  // Rate limit exceeded (UX3)
                                  if (error.response?.status === 429) {
                                    const retryAfter = error.response.headers?.['retry-after'];
                                    const secs = retryAfter ? ` Bitte warte ${retryAfter} Sekunden.` : '';
                                    toast.error(`Zu viele Uploads.${secs}`);
                                    handleUploadProgress(0);
                                    handleUploadComplete();
                                    return;
                                  }
                                  if (process.env.NODE_ENV !== 'production') console.error('Upload error:', error);
                                  toast.error('Fehler beim Hochladen');
                                  setIsUploading(false);
                                }
                              };
                              fileUploadHandler(file);
                            }
                            e.target.value = '';
                          }}
                        />
                        <Button
                          variant="default"
                          onClick={() => {
                            if (!canUploadNewFile()) {
                              toast.error('Bitte schließen Sie die aktuelle Datei ab: ' + getMissingSteps().join(', '));
                              return;
                            }
                            document.getElementById('add-more-file')?.click();
                          }}
                          disabled={isUploading || !canUploadNewFile()}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {isUploading ? 'Hochladen...' : 'Datei hinzufügen'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-gray-900">
                      <Package className="w-5 h-5" />
                      3D-Vorschau: {stlAnalysis.filename}
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                      Originalgröße: {stlAnalysis.dimensions.x.toFixed(1)} × {stlAnalysis.dimensions.y.toFixed(1)} × {stlAnalysis.dimensions.z.toFixed(1)} mm
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="w-full h-64 sm:h-80 lg:h-[700px] border border-gray-300 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
                                            <div className="mb-2">
                                              <button
                                                onClick={() => setShow3DPreview(!show3DPreview)}
                                                className="text-xs px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                                              >
                                                {show3DPreview ? '3D-Ansicht ausblenden' : '3D-Ansicht einblenden'}
                                              </button>
                                            </div>
                                            {show3DPreview && webglAvailable ? (
<Canvas 
                        camera={{ 
                          position: [100, 100, 100], 
                          fov: 45
                        }}
                        style={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #d1d5db 100%)' }}
                      >
                        <Suspense fallback={null}>
                          {/* Auto camera positioning */}
                          <AutoCameraPosition key={stlAnalysis?.id ?? "none"} stlAnalysis={stlAnalysis} scale={scale} />
                          
                          {/* Even 6-point lighting – every face equally bright */}
                          <ambientLight intensity={1.2} />
                          <directionalLight position={[  0,  100,   0]} intensity={0.6} />
                          <directionalLight position={[  0, -100,   0]} intensity={0.5} />
                          <directionalLight position={[100,    0,   0]} intensity={0.5} />
                          <directionalLight position={[-100,   0,   0]} intensity={0.5} />
                          <directionalLight position={[  0,    0, 100]} intensity={0.5} />
                          <directionalLight position={[  0,    0,-100]} intensity={0.5} />
                          
                          {/* Build plate */}
                          <SimpleBuildPlate printerLimits={printerLimits} materialType={materialType} />
                          
                          {stlUrl && (
                            <SimpleSTLMesh
                              stlUrl={stlUrl}
                              scale={scale}
                              selectedColor={selectedColor}
                            />
                          )}
                          
                          {/* Camera controls */}
                          <OrbitControls
                            makeDefault
                            enablePan
                            enableZoom
                            enableRotate
                            minDistance={5}
                            maxDistance={500}
                          />
                        </Suspense>
                      </Canvas>
                      ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-100 text-gray-600">
                        WebGL ist in deinem Browser deaktiviert oder nicht verfuegbar. Bitte aktiviere WebGL oder nutze einen anderen Browser/Geraet.
                      </div>
                      )}
                      
                      {/* BambuLab-style overlays */}
                      <div className="absolute top-4 left-4 bg-black bg-opacity-70 text-white text-sm p-3 rounded">
                        <div className="space-y-1">
                          <div>📄 {stlAnalysis.filename}</div>
                          <div>📏 {stlAnalysis.dimensions.x.toFixed(1)}×{stlAnalysis.dimensions.y.toFixed(1)}×{stlAnalysis.dimensions.z.toFixed(1)} mm</div>
                          <div>🔍 {(scale * 100).toFixed(1)}% Skalierung</div>
                        </div>
                      </div>
                      
                      <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white text-xs p-2 rounded">
                        <div>🖱️ Drehen • 🔍 Zoomen • 📱 Verschieben</div>
                      </div>
                      <div className="absolute bottom-4 left-4 text-gray-400 text-xs select-none pointer-events-none">
                        © {new Date().getFullYear()} 3D Fabrik
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Upload Instructions Card */}
                <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-gray-900">
                      <Info className="w-5 h-5" />
                      So funktioniert der Upload-Prozess
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-gray-700 space-y-4">
                    <p className="text-sm font-medium text-gray-800">
                      Für beste Ergebnisse folgen Sie bitte diesen Schritten für jede hochgeladene Datei:
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          1
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">STL-Datei hochladen</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Ziehen Sie Ihre STL-Datei per Drag & Drop in das Upload-Feld oder klicken Sie, um eine Datei auszuwählen. 
                            Die Datei wird automatisch auf Druckbarkeit geprüft.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          2
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Material und Farbe wählen</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Wählen Sie im rechten Panel das gewünschte Druckverfahren, Material und die Farbe aus. 
                            Diese Auswahl beeinflusst die Druckqualität und den Preis.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-red-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          3
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Slicer-Berechnung abwarten</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Nach der Material-Auswahl startet automatisch die Slicer-Berechnung. 
                            Sie sehen die voraussichtliche Druckzeit, benötigtes Material und den finalen Preis.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 w-7 h-7 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          ✓
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Weitere Dateien hinzufügen (optional)</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Erst nachdem alle 3 Schritte abgeschlossen sind, können Sie weitere STL-Dateien hochladen. 
                            Dies verhindert Preisfehler und stellt sicher, dass jedes Teil korrekt konfiguriert ist.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex gap-2 items-start">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-yellow-800">
                          <strong>Wichtig:</strong> Sie müssen jeden Upload vollständig abschließen 
                          (Material, Farbe, Slicer-Berechnung), bevor Sie die nächste Datei hochladen können. 
                          Dies garantiert korrekte Preisberechnungen für jedes Teil.
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Right Column - Configuration (1 column width) */}
          <div className="order-1 lg:order-3 lg:col-span-1 space-y-6">
            {materials && stlFiles.length > 0 && (
              <div className="relative">
                <MaterialSelector
                  materials={materials}
                  selectedMaterial={selectedMaterial}
                  onMaterialChange={handleMaterialChange}
                  selectedColor={selectedColor}
                  onColorChange={handleColorChange}
                />
                {isSlicing && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm border border-red-200 shadow-lg">
                    <div className="w-full px-6 space-y-3 text-center">
                      <p className="text-sm font-semibold text-gray-800">⚙️ Slicer-Berechnung läuft…</p>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-red-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${slicerProgress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{slicerProgress}%</span>
                        <span>
                          {(() => {
                            const remaining = slicerEstimated - slicerElapsed;
                            if (remaining <= 0) return 'fast fertig…';
                            if (remaining < 10) return `Noch ~${remaining} Sek.`;
                            return `Noch ca. ${remaining} Sek.`;
                          })()}
                        </span>
                        <span>{slicerElapsed}s</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {stlAnalysis && printerLimits && (
              <ScaleControl
                scale={scale}

                onScaleChange={handleScaleChange}
                dimensions={stlAnalysis.dimensions}
                printerLimits={printerLimits}
                materialType={materialType}
              />
            )}


            {stlAnalysis && materials && materials.filament && materials.filament[selectedMaterial] && (
              <InfillControl
                infill={infill}
                onInfillChange={handleInfillChange}
              />
            )}

            {stlAnalysis && selectedFile && selectedMaterial && (
              <SlicerPanel
                key={stlAnalysis?.id}
                stlFile={selectedFile}
                scale={scale}
                material={selectedMaterial}
                infill={infill}
                quote={quote}
                onUploadComplete={uploadComplete}
                isSlicing={isSlicing}
                onSlicingChange={setIsSlicing}
                onProgressChange={setSlicerProgress}
                onElapsedChange={setSlicerElapsed}
                onEstimatedChange={setSlicerEstimated}
                onCalculate={(results) => {
                  if (process.env.NODE_ENV !== "production") console.log("Slice results:", results);
                  if (currentFileIndex !== null) {
                    setStlFiles(prev => {
                      const updated = [...prev];
                      updated[currentFileIndex] = {
                        ...updated[currentFileIndex],
                        slicerResults: results
                      };
                      return updated;
                    });
                  }
                  toast.success("Slicer-Berechnung abgeschlossen");
                }}
              />
            )}

            <PriceDisplay
              quote={quote}
              isCalculating={isCalculating}
              hasSlicerResults={!!slicerResults}
              stlAnalysis={stlAnalysis}
              quantity={quantity}
            />
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-[1920px] mx-auto px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-1">
          <span className="text-xs text-gray-500">
            © {new Date().getFullYear()}&nbsp;
            <span className="font-semibold text-gray-700">3D Fabrik</span>
            &nbsp;– Gemäß §19 UStG wird keine Umsatzsteuer berechnet.
          </span>
          <a
            href={ODOO_BASE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-500 hover:text-red-700 transition-colors"
          >
            www.3dfabrikshop.de
          </a>
        </div>
      </footer>
    </div>
  );
}

function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithBoundary;
